import { useState, useEffect } from 'react';
import { hysteriFetch, wrappers } from 'hysterics';
import logger from 'ws-scripts/modules/logger';
import { isBrowser } from 'ws-scripts/modules/environment';

const gmPaymentsFetcher = hysteriFetch('ws-detailed-pricing-gm-payments');
const CLIENT_TIMEOUT = 5000;
const CLIENT_RETRIES = 1;

/**
 * Base empty result shape shared by finance and lease transformations.
 */
const EMPTY_PAYMENT_RESULT = {
	optIn: false,
	payment: null,
	term: null,
	dueAtSigning: null,
	interestRate: null,
	marketingLabel: null,
	disclosure: null,
	showContactDealer: false
};

/**
 * Parse payment data string, handling over-escaped JSON from VCDA API
 * @param {string} dataString - The encoded JSON string
 * @returns {Object|null} Parsed object or null if parsing fails
 */
const parsePaymentDataString = (dataString) => {
	// If already an object, return as-is
	if (typeof dataString === 'object' && dataString !== null) {
		return dataString;
	}

	// If not a string at this point, we can't parse it
	if (typeof dataString !== 'string') {
		throw new Error(`Expected string or object, got ${typeof dataString}`);
	}

	try {
		// First, try to unwrap if the entire input is a JSON string literal
		let inner;
		try {
			inner = JSON.parse(dataString);
		} catch {
			// Not wrapped in JSON string literal, use as-is
			inner = dataString;
		}

		// If we got an object after first parse, we're done
		if (typeof inner === 'object' && inner !== null) {
			return inner;
		}

		// Normalize escaped characters (handle over-escaped JSON)
		// Replace escaped quotes first, then escaped backslashes
		const normalized = inner.replace(/\\"/g, '"').replace(/\\\\/g, '\\');

		// Parse the normalized string to object
		return JSON.parse(normalized);
	} catch (err) {
		throw new Error(`Failed to parse payment data: ${err.message}`);
	}
};

/**
 * Transform finance data according to GM Display Rules
 * Finance Rule: Display lowest monthly payment for longest finance term where payment > $0
 * Uses direct payment fields from VCDA (monthlyPaymentNetPrice / monthlyPaymentNetPriceDealerFees)
 * Falls back to calculated payment (paymentPerThousand × netPrice) if direct fields are absent
 * @param {Object} data - Raw VCDA features response
 * @param {boolean} includeDealerFees - Whether to use dealer fees variant
 * @returns {Object} Transformed finance data
 */
const transformFinanceData = (data, includeDealerFees = false) => {
	// Test data had different opt-in field names, so check both
	const optIn = data.gmFinanceOptIn ?? data.financeOfferOptedIn ?? false;
	const financeMathBoxes = data.gmFinanceMathBoxes || [];

	// Not opted in at all
	if (!optIn) {
		return EMPTY_PAYMENT_RESULT;
	}

	// Opted in but no math boxes - show Contact Dealer
	if (financeMathBoxes.length === 0) {
		return { ...EMPTY_PAYMENT_RESULT, optIn, showContactDealer: true };
	}

	// Get monthly payment for each term using direct fields or fallback calculation
	const termsWithPayments = financeMathBoxes
		.map((term) => {
			// Prefer direct payment fields from VCDA
			const directPayment = includeDealerFees
				? term.monthlyPaymentNetPriceDealerFees
				: term.monthlyPaymentNetPrice;

			let payment;
			if (directPayment != null && directPayment > 0) {
				payment = directPayment;
			} else {
				// Fallback: calculate from paymentPerThousand × net price
				const netPriceItem = term.termMathBox?.find(
					(item) =>
						item.type ===
						(includeDealerFees
							? 'net_price_with_dealer_fees'
							: 'net_price')
				);
				const netPrice = netPriceItem?.amount || 0;
				payment = (netPrice / 1000) * (term.paymentPerThousand || 0);
			}

			return { ...term, payment };
		})
		.filter((term) => term.payment > 0); // Only valid payments

	// Check if all terms have invalid payments (Contact Dealer scenario)
	if (termsWithPayments.length === 0) {
		return { ...EMPTY_PAYMENT_RESULT, optIn, showContactDealer: true };
	}

	// Sort by lowest payment first, then by longest term
	if (termsWithPayments.length > 1) {
		termsWithPayments.sort((a, b) => {
			if (a.payment === b.payment) {
				return b.term - a.term; // Longest term wins on tie
			}
			return a.payment - b.payment; // Lowest payment wins
		});
	}

	const selectedTerm = termsWithPayments[0];

	return {
		optIn,
		payment: Math.round(selectedTerm.payment),
		term: selectedTerm.term,
		interestRate: selectedTerm.interestRate ?? null,
		marketingLabel: selectedTerm.marketingLabel || null,
		disclosure: selectedTerm.disclosure?.value || null,
		showContactDealer: false
	};
};

/**
 * Transform lease data according to GM Display Rules
 * Lease Rule: Display lowest monthly payment where the relevant contactDealer flag is false
 * Uses direct payment fields (monthlyPaymentUltraLowMileageNetPrice / DealerFees)
 * Constructs fallback marketingLabel: "$X/mo for Y months, $Z due at signing"
 * @param {Object} data - Raw VCDA features response
 * @param {boolean} includeDealerFees - Whether to use dealer fees variant
 * @param {Object} labels - Labels map for i18n (optional)
 * @returns {Object} Transformed lease data
 */
const transformLeaseData = (data, includeDealerFees = false, labels = null) => {
	// Test data had different opt-in field names, so check both
	const optIn = data.gmLeaseOptIn ?? data.leaseOfferOptedIn ?? false;
	const leaseMathBoxes = data.gmLeaseMathBoxes || [];

	// Not opted in at all
	if (!optIn) {
		return EMPTY_PAYMENT_RESULT;
	}

	// Opted in but no math boxes - show Contact Dealer
	if (leaseMathBoxes.length === 0) {
		return { ...EMPTY_PAYMENT_RESULT, optIn, showContactDealer: true };
	}

	// Filter terms where the relevant contactDealer flag is false
	const contactDealerField = includeDealerFees
		? 'contactDealerWithFees'
		: 'contactDealerWithoutFees';

	const validTerms = leaseMathBoxes.filter((term) => {
		// If the field exists, check it; if absent, treat term as valid (backward compat)
		if (contactDealerField in term) {
			return term[contactDealerField] === false;
		}
		return true;
	});

	// Check if all terms require "Contact Dealer"
	if (validTerms.length === 0) {
		return { ...EMPTY_PAYMENT_RESULT, optIn, showContactDealer: true };
	}

	// Get monthly payment and due at signing for each valid term using direct fields
	const termsWithPayments = validTerms
		.map((term) => {
			const payment = includeDealerFees
				? term.monthlyPaymentUltraLowMileageNetPriceDealerFees
				: term.monthlyPaymentUltraLowMileageNetPrice;

			const dueAtSigning = includeDealerFees
				? term.dueAtSigningUltraLowMileageNetPriceDealerFees
				: term.dueAtSigningUltraLowMileageNetPrice;

			return {
				...term,
				payment: payment ?? 0,
				dueAtSigning: dueAtSigning ?? null
			};
		})
		.filter((term) => term.payment > 0);

	// If no terms have valid direct payment amounts, show Contact Dealer
	if (termsWithPayments.length === 0) {
		return { ...EMPTY_PAYMENT_RESULT, optIn, showContactDealer: true };
	}

	// Sort by lowest payment
	if (termsWithPayments.length > 1) {
		termsWithPayments.sort((a, b) => a.payment - b.payment);
	}

	const selectedTerm = termsWithPayments[0];
	const roundedPayment = Math.round(selectedTerm.payment);
	const roundedDueAtSigning =
		selectedTerm.dueAtSigning != null
			? Math.round(selectedTerm.dueAtSigning)
			: null;

	// Construct fallback marketingLabel if none provided
	// Uses i18n template: "Lease for {0}/mo. for {1} mos. w/{2} due at signing"
	let marketingLabel = selectedTerm.marketingLabel || null;
	if (!marketingLabel) {
		marketingLabel = labels
			.get('LEASE_FOR_X_PER_MONTH_FOR_X_MONTHS_WITH_X_DUE_AT_SIGNING')
			.replace('{0}', `$${roundedPayment}`)
			.replace('{1}', selectedTerm.term)
			.replace(
				'{2}',
				roundedDueAtSigning != null ? `$${roundedDueAtSigning}` : ''
			);
	}

	return {
		optIn,
		payment: roundedPayment,
		term: selectedTerm.term,
		dueAtSigning: roundedDueAtSigning,
		interestRate: selectedTerm.interestRate ?? null,
		marketingLabel,
		disclosure: selectedTerm.disclosure?.value || null,
		showContactDealer: false
	};
};

/**
 * Transform VCDA Features response according to GM Display Rules
 * @param {Object} data - Raw VCDA features response
 * @param {boolean} includeDealerFees - Whether to use dealer fees variants
 * @param {Object} labels - Labels map for i18n (optional)
 * @returns {Object} Transformed payment data
 */
const transformGMPaymentData = (
	data,
	includeDealerFees = false,
	labels = null
) => {
	if (!data) return null;

	const financeData = transformFinanceData(data, includeDealerFees);
	const leaseData = transformLeaseData(data, includeDealerFees, labels);

	return {
		finance: financeData,
		lease: leaseData
	};
};

// Export transformation functions for testing
export {
	parsePaymentDataString,
	transformFinanceData,
	transformLeaseData,
	transformGMPaymentData
};

/**
 * Custom hook to fetch and transform GM payment data
 * @param {Object} params
 * @param {string} params.vehicleId - Vehicle UUID
 * @param {string} params.accountId - Account ID (optional)
 * @param {string} params.locale - Locale for payment data (e.g., 'en_US', 'es_MX', 'fr_CA')
 * @param {boolean} params.enabled - Whether to fetch (for conditional fetching, defaults to false)
 * @param {string} params.eligibilityReason - Debug reason for eligibility (optional)
 * @returns {Object} { gmPayments, isLoading, error }
 */
export const useGMPayments = ({
	vehicleId,
	accountId,
	locale = 'en_US',
	enabled = false,
	includeDealerFees = false,
	eligibilityReason = '',
	labels = null
}) => {
	const [gmPayments, setGmPayments] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);

	// Debug logging for eligibility (only logs with ?_mode=debug in browser)
	useEffect(() => {
		if (!isBrowser || !eligibilityReason) return;

		let urlParams = {};
		if (typeof window.DDC?.getUrlParams === 'function') {
			urlParams = window.DDC.getUrlParams();
		}

		if (urlParams._mode === 'debug') {
			/* eslint-disable no-console */
			if (!enabled) {
				console.log(`GM Payments Not Eligible: ${eligibilityReason}`);
			} else {
				console.log(`GM Payments Eligible: ${eligibilityReason}`);
			}
			/* eslint-enable no-console */
		}
	}, [enabled, eligibilityReason]);

	useEffect(() => {
		// Don't fetch if disabled or no vehicleId
		if (!enabled || !vehicleId) {
			return;
		}

		const fetchGMPaymentData = async () => {
			setIsLoading(true);
			setError(null);

			try {
				// Use widget proxy path (not /api/gm-payments)
				const requestUrl =
					'/api/widget/ws-detailed-pricing/gm-payments';
				const requestBody = {
					vehicleId,
					accountId
				};

				// POST request with timeout and retry wrappers
				const response = await gmPaymentsFetcher(
					requestUrl,
					{
						method: 'POST',
						headers: {
							'Content-Type': 'application/json;charset=UTF-8'
						},
						body: JSON.stringify(requestBody)
					},
					{
						[wrappers.timeout.contextKey]: {
							timeout: CLIENT_TIMEOUT
						},
						[wrappers.retry.contextKey]: {
							retries: CLIENT_RETRIES
						}
					}
				);

				if (!response) {
					// No response - set empty state
					setGmPayments(null);
					return;
				}

				if (response.errors) {
					throw new Error(response.errors.message || 'Unknown error');
				}

				// Extract locale-specific payment data from gmTier1PaymentInfo
				const gmTier1PaymentInfo =
					response.data?.gmTier1PaymentInfo || {};
				let paymentData = gmTier1PaymentInfo[locale];

				// Fallback to en_US if requested locale is not available
				if (!paymentData && locale !== 'en_US') {
					logger.warn(
						`GM_PAYMENTS/CLIENT/LOCALE_FALLBACK: Locale '${locale}' not found, falling back to 'en_US'`
					);
					paymentData = gmTier1PaymentInfo.en_US;
				}

				// If still no data, log and return null
				if (!paymentData) {
					logger.warn(
						`GM_PAYMENTS/CLIENT/NO_LOCALE_DATA: No payment data found for locale '${locale}' or fallback 'en_US'`
					);
					setGmPayments(null);
					return;
				}

				// Parse payment data if it's a string (encoded JSON)
				if (typeof paymentData === 'string') {
					try {
						paymentData = parsePaymentDataString(paymentData);
					} catch (parseErr) {
						logger.error(
							`GM_PAYMENTS/CLIENT/PARSE_ERROR: ${parseErr.message}`
						);
						setGmPayments(null);
						return;
					}
				}

				// Transform the data according to GM Display Rules
				const transformedData = transformGMPaymentData(
					paymentData,
					includeDealerFees,
					labels
				);
				setGmPayments(transformedData);
			} catch (err) {
				const errorMessage = `GM_PAYMENTS/CLIENT/FETCH_ERROR: ${err.message}`;
				logger.error(errorMessage);
				setError(errorMessage);
				setGmPayments(null);
			} finally {
				setIsLoading(false);
			}
		};

		fetchGMPaymentData();
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
	}, [vehicleId, accountId, locale, enabled, includeDealerFees]);

	return { gmPayments, isLoading, error };
};
